import { useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { ErrorReportDialog } from "@/components/error-report-dialog"
import { AlertCircle, ArrowRight, Bug, Check, Copy, Lock, RefreshCw } from "@/components/icons"
import { PlanUpgradeNotice } from "@/features/fair-use/components/plan-upgrade-notice"
import { useFairUseStore } from "@/features/fair-use/fair-use-store"
import { cn } from "@/lib/utils"

import { SystemMessageLayout } from "./system-message-layout"
import { Button } from "../../../components/ui/button"

export type ErrorProps = {
  error: string
  errorId?: string
  /**
   * Stable machine-readable id for `error` (`ChatAppResponse.error_code`).
   * `"fair_use_blocked"` swaps the destructive error card for the budget gate
   * below — see {@link FairUseBlockedNotice} — and `"plan_upgrade_required"`
   * swaps it for the plan card. Both are policy states, not failures, so
   * neither may fall through to Retry/Report.
   */
  errorCode?: string
  sessionId?: string
  onRetry?: () => void
  onContinue?: () => Promise<void>
  canContinue?: boolean
}

/**
 * The in-chat fair-use gate (#2625): what a hard-blocked user sees in place of
 * the answer they asked for.
 *
 * Deliberately NOT the destructive error card. Nothing failed — the month's
 * budget is spent, which is a policy state the user can act on — so it takes
 * the warning treatment and the same "Paused" vocabulary as the composer
 * popover's blocked badge (DESIGN.md: destructive red is for errors and
 * deletes; amber is the warning surface).
 *
 * No Retry: the gate refuses every turn until budget is granted or the month
 * resets, so retrying can only reproduce this card. No "Report issue" either:
 * this is the system working as configured, and a bug report from here is
 * noise for whoever triages it. The one action that can change the outcome —
 * asking an admin for more budget — is the one button offered; it opens the
 * request-more dialog mounted alongside the composer's usage indicator, the
 * same dialog the popover's CTA opens.
 */
function FairUseBlockedNotice() {
  const { t } = useTranslation()
  const openRequestDialog = useFairUseStore((state) => state.openRequestDialog)

  return (
    <SystemMessageLayout>
      <SystemMessageLayout.Content>
        <div
          className="border-warning/30 bg-warning/10 space-y-3 rounded-lg border p-4"
          data-testid="fair-use-blocked-notice"
        >
          <div className="flex items-start gap-3">
            <Lock className="text-warning-foreground mt-0.5 size-5 shrink-0" />
            <div className="flex-1 space-y-1">
              <p className="text-foreground text-sm font-bold">
                {t("fairUse.chat.blockedTitle", "AI budget used up")}
              </p>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {t("fairUse.chat.blockedNotice")}
              </p>
            </div>
          </div>
          <div className="border-border/30 border-t pt-3">
            <Button
              className="w-full gap-1.5 text-xs sm:w-auto"
              data-testid="fair-use-blocked-request-more"
              size="sm"
              onClick={() => openRequestDialog()}
            >
              {t("fairUse.popover.requestMore")}
            </Button>
          </div>
        </div>
      </SystemMessageLayout.Content>
    </SystemMessageLayout>
  )
}

export function Error({
  error,
  errorId,
  errorCode,
  sessionId,
  onRetry,
  onContinue,
  canContinue,
}: ErrorProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  if (errorCode === "fair_use_blocked") {
    return <FairUseBlockedNotice />
  }

  // Fair use round 2 (#3087): a plan-locked feature never produces a generic
  // error (spec invariant). Retry and "Report issue" would both be wrong —
  // the refusal is the configuration working as intended.
  if (errorCode === "plan_upgrade_required") {
    return (
      <SystemMessageLayout>
        <SystemMessageLayout.Content>
          <PlanUpgradeNotice />
        </SystemMessageLayout.Content>
      </SystemMessageLayout>
    )
  }

  if (errorCode === "conversation_forgotten") {
    return (
      <SystemMessageLayout>
        <SystemMessageLayout.Content>
          <div
            className="border-border/30 bg-muted/30 rounded-lg border p-4"
            data-testid="conversation-forgotten-notice"
          >
            <p className="text-foreground/80 text-sm leading-relaxed">
              {t("personalization.memoryPanel.prism.chatForgotten")}
            </p>
          </div>
        </SystemMessageLayout.Content>
      </SystemMessageLayout>
    )
  }

  const handleCopyErrorId = async () => {
    if (!errorId) return
    await navigator.clipboard.writeText(errorId)
    setCopied(true)
    toast.success("Error reference copied to clipboard")
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <SystemMessageLayout>
      <SystemMessageLayout.Content>
        <div className="border-destructive/20 bg-muted/30 space-y-3 rounded-lg border p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-destructive/60 mt-0.5 size-5 shrink-0" />
            <div className="flex-1 space-y-2">
              <p className="text-foreground/80 text-sm leading-relaxed">{error}</p>
              {errorId && (
                <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground/70 text-xs font-medium">
                      Error Reference:
                    </span>
                    <code className="text-muted-foreground/60 font-mono text-xs break-all">
                      {errorId}
                    </code>
                  </div>
                  <Button
                    className="group h-6 gap-1 self-start px-2 text-xs"
                    size="sm"
                    variant="ghost"
                    onClick={handleCopyErrorId}
                  >
                    {copied ? (
                      <>
                        <Check className="text-success size-3" />
                        <span className="text-success">Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="size-3" />
                        <span>Copy</span>
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="border-border/30 flex flex-col gap-2 border-t pt-3 sm:flex-row sm:flex-wrap">
            {onRetry && (
              <Button
                className={cn("w-full gap-1.5 text-xs sm:w-auto")}
                size="sm"
                variant="secondary"
                onClick={onRetry}
              >
                <RefreshCw className="size-3.5" />
                {t("answer.retry")}
              </Button>
            )}
            {canContinue && onContinue && (
              <Button
                className={cn("w-full gap-1.5 text-xs sm:w-auto")}
                size="sm"
                variant="ghost"
                onClick={() => void onContinue()}
              >
                {t("answer.continue")}
                <ArrowRight className="size-3.5" />
              </Button>
            )}
            <ErrorReportDialog
              errorId={errorId}
              sessionId={sessionId}
              trigger={
                <Button
                  className={cn("w-full gap-1.5 text-xs sm:w-auto")}
                  size="sm"
                  variant="outline"
                >
                  <Bug className="size-3.5" />
                  {t("answer.reportIssue", { defaultValue: "Report issue" })}
                </Button>
              }
            />
          </div>
        </div>
      </SystemMessageLayout.Content>
    </SystemMessageLayout>
  )
}
